import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Link2, BarChart3, Sparkles, FileSpreadsheet, FileText, Lock, Unlock, ArrowLeft, Loader2, GripVertical } from "lucide-react";
import { useApp } from "@/app/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { API, apiErr } from "@/lib/api";

const QTYPES = [["short", "Réponse courte"], ["paragraph", "Paragraphe"], ["single", "Choix unique"], ["multiple", "Choix multiples"], ["scale", "Échelle (1-5)"], ["date", "Date"]];

export default function Forms() {
  const { setCredits, refreshMeta } = useApp();
  const [forms, setForms] = useState([]);
  const [cur, setCur] = useState(null);
  const [tab, setTab] = useState("build");
  const [results, setResults] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const load = () => api.get("/forms").then((r) => setForms(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    const { data } = await api.post("/forms", { title: "Nouveau questionnaire", intro: "", instructions: "", questions: [] });
    load(); openForm(data.id);
  };
  const openForm = async (id) => { const { data } = await api.get(`/forms/${id}`); setCur(data); setTab("build"); setResults(null); };
  const save = async () => { const { data } = await api.put(`/forms/${cur.id}`, cur); setCur(data); load(); toast.success("Enregistré"); };
  const toggle = async () => { const { data } = await api.post(`/forms/${cur.id}/toggle`); setCur({ ...cur, is_open: data.is_open }); load(); };
  const del = async (id) => { await api.delete(`/forms/${id}`); setCur(null); load(); };
  const addQ = (type) => setCur({ ...cur, questions: [...cur.questions, { id: Math.random().toString(36).slice(2, 10), type, label: "", options: type === "single" || type === "multiple" ? ["Option 1"] : [], required: false }] });
  const updQ = (i, patch) => { const qs = [...cur.questions]; qs[i] = { ...qs[i], ...patch }; setCur({ ...cur, questions: qs }); };
  const delQ = (i) => setCur({ ...cur, questions: cur.questions.filter((_, x) => x !== i) });

  const shareLink = (f) => `${window.location.origin}/f/${f.public_id}`;
  const copyLink = (f) => { navigator.clipboard.writeText(shareLink(f)); toast.success("Lien copié"); };

  const loadResults = async () => { const { data } = await api.get(`/forms/${cur.id}/responses`); setResults(data); setTab("results"); };
  const analyze = async () => {
    setAnalyzing(true);
    try { const { data } = await api.post(`/forms/${cur.id}/analyze`); setCur({ ...cur, analysis: data.analysis }); setCredits(data.credits); refreshMeta(); toast.success("Analyse terminée"); }
    catch (e) { toast.error(apiErr(e)); } finally { setAnalyzing(false); }
  };
  const dl = (path) => { const t = localStorage.getItem("lisungi_token"); window.open(`${API}${path}?auth=${t}`, "_blank"); };

  if (!cur) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8 animate-fade-up">
        <div className="flex items-center justify-between">
          <div><h1 className="font-serif text-3xl font-bold text-navy">Forms / Questionnaires</h1><p className="text-muted2 mt-1">Créez des enquêtes pour vos TFC, mémoires, thèses et études de terrain.</p></div>
          <Button onClick={create} className="bg-navy text-white rounded-full gap-2" data-testid="form-create-btn"><Plus className="h-4 w-4" /> Nouveau</Button>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          {forms.map((f) => (
            <div key={f.id} className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid={`form-card-${f.id}`}>
              <div className="flex items-start justify-between">
                <button onClick={() => openForm(f.id)} className="text-left min-w-0"><p className="font-semibold text-navy truncate">{f.title}</p><p className="text-xs text-muted2 mt-1">{f.questions.length} questions · {f.response_count} réponses</p></button>
                <span className={`text-xs font-mono px-2 py-0.5 rounded ${f.is_open ? "bg-green-50 text-green-700" : "bg-gray-100 text-muted2"}`}>{f.is_open ? "Ouvert" : "Fermé"}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => copyLink(f)} className="rounded-full gap-1 text-xs"><Link2 className="h-3 w-3" /> Lien</Button>
                <Button size="sm" variant="outline" onClick={() => openForm(f.id)} className="rounded-full gap-1 text-xs border-navy text-navy">Ouvrir</Button>
                <button onClick={() => del(f.id)} className="ml-auto text-muted2 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          {forms.length === 0 && <p className="text-muted2 text-sm col-span-full text-center py-10">Aucun questionnaire. Créez-en un.</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 animate-fade-up">
      <button onClick={() => { setCur(null); load(); }} className="text-sm text-muted2 hover:text-navy flex items-center gap-1 mb-4"><ArrowLeft className="h-4 w-4" /> Mes questionnaires</button>
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant={tab === "build" ? "default" : "outline"} onClick={() => setTab("build")} className={`rounded-full ${tab === "build" ? "bg-navy text-white" : ""}`} data-testid="tab-build">Éditeur</Button>
        <Button variant="outline" onClick={loadResults} className="rounded-full gap-1" data-testid="tab-results"><BarChart3 className="h-4 w-4" /> Résultats ({cur.response_count})</Button>
        <Button variant="outline" onClick={() => copyLink(cur)} className="rounded-full gap-1"><Link2 className="h-4 w-4" /> Partager</Button>
        <Button variant="outline" onClick={toggle} className="rounded-full gap-1">{cur.is_open ? <><Lock className="h-4 w-4" /> Fermer</> : <><Unlock className="h-4 w-4" /> Rouvrir</>}</Button>
        <Button onClick={save} className="rounded-full bg-lisungi-yellow text-navy ml-auto" data-testid="form-save">Enregistrer</Button>
      </div>

      {tab === "build" && (
        <div className="mt-5 space-y-4">
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 space-y-3">
            <Input value={cur.title} onChange={(e) => setCur({ ...cur, title: e.target.value })} className="font-serif text-xl font-bold text-navy border-0 px-0 focus-visible:ring-0" data-testid="form-title" />
            <Textarea value={cur.intro} onChange={(e) => setCur({ ...cur, intro: e.target.value })} rows={2} placeholder="Introduction" />
            <Textarea value={cur.instructions} onChange={(e) => setCur({ ...cur, instructions: e.target.value })} rows={2} placeholder="Consignes" />
          </div>
          {cur.questions.map((q, i) => (
            <div key={q.id} className="bg-white rounded-2xl border border-[#E5E7EB] p-4" data-testid={`q-${i}`}>
              <div className="flex items-center gap-2">
                <GripVertical className="h-4 w-4 text-muted2" />
                <Input value={q.label} onChange={(e) => updQ(i, { label: e.target.value })} placeholder={`Question ${i + 1}`} className="flex-1" data-testid={`q-label-${i}`} />
                <Select value={q.type} onValueChange={(v) => updQ(i, { type: v, options: (v === "single" || v === "multiple") && !q.options.length ? ["Option 1"] : q.options })}>
                  <SelectTrigger className="w-40 h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{QTYPES.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => delQ(i)} className="text-muted2 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              {(q.type === "single" || q.type === "multiple") && (
                <div className="mt-3 pl-6 space-y-2">
                  {q.options.map((o, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <Input value={o} onChange={(e) => { const opts = [...q.options]; opts[oi] = e.target.value; updQ(i, { options: opts }); }} className="h-8 text-sm w-64" />
                      <button onClick={() => updQ(i, { options: q.options.filter((_, x) => x !== oi) })} className="text-muted2 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  <button onClick={() => updQ(i, { options: [...q.options, `Option ${q.options.length + 1}`] })} className="text-xs text-navy hover:underline">+ Ajouter une option</button>
                </div>
              )}
              <label className="flex items-center gap-2 mt-3 pl-6 text-xs text-muted2"><input type="checkbox" checked={q.required} onChange={(e) => updQ(i, { required: e.target.checked })} /> Obligatoire</label>
            </div>
          ))}
          <div className="bg-white rounded-2xl border border-dashed border-[#E5E7EB] p-4">
            <p className="text-xs text-muted2 mb-2">Ajouter une question :</p>
            <div className="flex flex-wrap gap-2">{QTYPES.map(([v, l]) => <Button key={v} size="sm" variant="outline" onClick={() => addQ(v)} className="rounded-full text-xs" data-testid={`add-q-${v}`}>+ {l}</Button>)}</div>
          </div>
        </div>
      )}

      {tab === "results" && results && (
        <div className="mt-5 space-y-4">
          <div className="flex gap-2 flex-wrap">
            <Button onClick={analyze} disabled={analyzing} className="bg-navy text-white rounded-full gap-1" data-testid="form-analyze">{analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Analyser avec Lisungi AI</Button>
            <Button variant="outline" onClick={() => dl(`/forms/${cur.id}/export/xlsx`)} className="rounded-full gap-1" data-testid="export-xlsx"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
            <Button variant="outline" onClick={() => dl(`/forms/${cur.id}/analyze/docx`)} className="rounded-full gap-1" data-testid="export-docx"><FileText className="h-4 w-4" /> Rapport Word</Button>
          </div>
          <p className="text-sm text-muted2">{results.count} réponse(s) collectée(s).</p>
          {results.questions.map((q) => {
            const a = results.aggregate[q.id];
            return (
              <div key={q.id} className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
                <p className="font-semibold text-navy">{q.label || "(sans titre)"}</p>
                {a?.type === "counts" ? (
                  <div className="mt-2 space-y-1">{Object.entries(a.data).length === 0 ? <p className="text-xs text-muted2">Aucune donnée.</p> : Object.entries(a.data).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 text-sm"><span className="w-40 truncate">{k}</span><div className="flex-1 bg-lisungi-bg rounded h-3"><div className="bg-lisungi-yellow h-3 rounded" style={{ width: `${Math.min(100, (v / results.count) * 100)}%` }} /></div><span className="text-xs font-mono">{v}</span></div>
                  ))}</div>
                ) : (
                  <div className="mt-2 space-y-1">{(a?.data || []).length === 0 ? <p className="text-xs text-muted2">Aucune réponse.</p> : a.data.slice(0, 30).map((t, i) => <p key={i} className="text-sm text-ink border-l-2 border-[#E5E7EB] pl-2">{t}</p>)}</div>
                )}
              </div>
            );
          })}
          {cur.analysis && (
            <div className="bg-white rounded-2xl border border-lisungi-yellow p-5">
              <p className="font-serif text-lg font-bold text-navy mb-2">Analyse Lisungi AI</p>
              <div className="text-sm text-ink whitespace-pre-wrap leading-relaxed">{cur.analysis}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
